import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getArchivedObjects, deleteArchivedObject, stripArchivedProject } from '../utils/storage';
import LoadingScreen from './LoadingScreen';
import { EmptyState } from './templates';
import './InspectionsPage.css';
import './ArchivedProjectsPage.css';

// Format a "YYYY-MM-DD" start date for display; passthrough if unparseable.
function fmtDate(s) {
  if (!s) return '—';
  const d = new Date(String(s) + 'T00:00:00');
  return isNaN(d) ? s : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Group key: an object is identified by its TYPE + ID combo, so two different
// object types that happen to share an ID never merge together.
const objKey = (o) => `${o.type || ''} ${o.object_tag || ''}`;

/* test site → object (type+id) → projects (rows with inspections). */
function buildTree(objects) {
  const byClient = new Map();
  for (const o of objects) {
    if (!byClient.has(o.client_id)) {
      byClient.set(o.client_id, { clientId: o.client_id, testSite: o.testSite, objs: new Map() });
    }
    const c = byClient.get(o.client_id);
    const k = objKey(o);
    if (!c.objs.has(k)) c.objs.set(k, []);
    c.objs.get(k).push(o);
  }
  const clients = [...byClient.values()].sort((a, b) => (a.testSite || '').localeCompare(b.testSite || ''));
  for (const c of clients) {
    c.objList = [...c.objs.values()]
      .map((rows) => {
        const sorted = [...rows].sort((a, b) => String(b.archivedAt || '').localeCompare(String(a.archivedAt || '')));
        return {
          type: sorted[0].type || '',
          tag: sorted[0].object_tag || '',
          latest: sorted[0],                       // supplies the object node + object-info
          projects: sorted.filter((r) => r.hasProject),
        };
      })
      .sort((a, b) => (a.tag || '').localeCompare(b.tag || '') || (a.type || '').localeCompare(b.type || ''));
  }
  return clients;
}

export default function ArchivedProjectsPage() {
  const navigate = useNavigate();
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageReady, setPageReady] = useState(false);
  const [del, setDel] = useState(null);   // { project, objectId, isOnly }
  const [askObject, setAskObject] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    return getArchivedObjects()
      .then((list) => setObjects(list))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    getArchivedObjects()
      .then((list) => { if (!cancelled) { setObjects(list); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (!pageReady) {
    return <LoadingScreen isLoading={loading} onDone={() => setPageReady(true)} />;
  }

  const clients = buildTree(objects);

  // One display row per project (or one "No archived projects" placeholder when
  // an object has none). Border model mirrors the inspections table with
  // group = object: light line between an object's projects, a 2px separator at
  // each object's end (+ the test-site rowspan), and no bottom on the final group.
  const rows = [];
  clients.forEach((c, ci) => {
    const clientIsLast = ci === clients.length - 1;
    const displayCount = c.objList.reduce((n, o) => n + Math.max(o.projects.length, 1), 0);
    let firstOfClient = true;
    c.objList.forEach((o, oi) => {
      const objIsLastOfTable = clientIsLast && oi === c.objList.length - 1;
      const projectRows = o.projects.length > 0 ? o.projects : [null];  // null = placeholder
      projectRows.forEach((p, pi) => {
        const projLast = pi === projectRows.length - 1;
        rows.push({
          key: p ? p.id : `empty-${c.clientId}-${objKey(o)}`,
          site: firstOfClient
            ? { label: c.testSite || '—', span: displayCount, bottom2: !clientIsLast }
            : null,
          object: pi === 0
            ? { label: [o.type, o.tag].filter(Boolean).join(' - ') || '—', span: projectRows.length, clientId: o.latest.client_id, objectId: o.latest.id, bottom2: !objIsLastOfTable }
            : null,
          top1: pi > 0,                              // light separator between an object's projects
          bottom2: projLast && !objIsLastOfTable,    // 2px group separator at object end
          project: p
            ? { label: `${fmtDate(p.startDate)}${p.projectNumber ? ` [${p.projectNumber}]` : ''}`, clientId: p.client_id, objectId: p.id, isOnly: o.projects.length === 1 }
            : null,
        });
        firstOfClient = false;
      });
    });
  });

  function requestDelete(project) {
    setDel({ project, isOnly: project.isOnly });
    setAskObject(false);
  }

  async function confirmDelete() {
    if (busy || !del) return;
    // If this is the only project for its object, ask whether to drop the
    // object shell too before doing anything.
    if (del.isOnly && !askObject) { setAskObject(true); return; }
    setBusy(true);
    try {
      await deleteArchivedObject(del.project.objectId);   // removes the object row entirely
      await load();
    } finally {
      setBusy(false); setDel(null); setAskObject(false);
    }
  }

  async function keepObjectShell() {
    if (busy || !del) return;
    setBusy(true);
    try {
      await stripArchivedProject(del.project.objectId);   // keep object, drop its project data
      await load();
    } finally {
      setBusy(false); setDel(null); setAskObject(false);
    }
  }

  return (
    <div className="page-container">
      <h1 className="arch-page-title">Archived Projects</h1>

      {clients.length === 0 ? (
        <EmptyState icon="📦">
          <h2>No archived projects</h2>
          <p>Objects you archive from the object page appear here, read-only.</p>
        </EmptyState>
      ) : (
        <div className="insp-table-wrapper">
          <table className="insp-main-table arch-table">
            <colgroup>
              <col className="arch-col-site" />
              <col className="arch-col-object" />
              <col className="arch-col-project" />
            </colgroup>
            <thead>
              <tr>
                <th>Test Site</th>
                <th>Object</th>
                <th>Project</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  {r.site && (
                    <td className={`arch-span arch-site-cell${r.site.bottom2 ? ' arch-b2' : ''}`} rowSpan={r.site.span}>
                      {r.site.label}
                    </td>
                  )}
                  {r.object && (
                    <td
                      className={`arch-span arch-object-cell${r.object.bottom2 ? ' arch-b2' : ''}`}
                      rowSpan={r.object.span}
                      onClick={() => navigate(`/client/${r.object.clientId}/object/${r.object.objectId}?view=summary`)}
                    >
                      {r.object.label}
                    </td>
                  )}
                  {r.project ? (
                    <td className={`arch-project-cell${r.top1 ? ' arch-t1' : ''}${r.bottom2 ? ' arch-b2' : ''}`}>
                      <div className="arch-project-inner">
                        <button
                          className="arch-project-link"
                          onClick={() => navigate(`/client/${r.project.clientId}/object/${r.project.objectId}`)}
                        >
                          {r.project.label}
                        </button>
                        <button
                          className="arch-delete-btn"
                          title="Delete this archived project"
                          aria-label="Delete archived project"
                          onClick={() => requestDelete(r.project)}
                        >
                          ✕
                        </button>
                      </div>
                    </td>
                  ) : (
                    <td className={`arch-project-cell arch-empty-cell${r.top1 ? ' arch-t1' : ''}${r.bottom2 ? ' arch-b2' : ''}`}>
                      No archived projects
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {del && (
        <div className="modal-overlay" onClick={() => !busy && setDel(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            {askObject ? (
              <>
                <h2 className="modal-title">Also delete the object?</h2>
                <p className="modal-body-text">
                  This is the only project for this object. Do you also want to permanently delete the
                  object? Choose <strong>No</strong> to keep the archived object (it will show
                  “No archived projects”).
                </p>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={keepObjectShell} disabled={busy}>No, keep object</button>
                  <button className="btn btn-danger" onClick={confirmDelete} disabled={busy}>
                    {busy ? 'Deleting…' : 'Yes, delete object'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="modal-title">Delete archived project?</h2>
                <p className="modal-body-text">
                  This permanently removes the project and all its inspections. This cannot be undone.
                </p>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setDel(null)} disabled={busy}>Cancel</button>
                  <button className="btn btn-danger" onClick={confirmDelete} disabled={busy}>
                    {busy ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
